import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import { LabeledSelect } from "../formPrimitives";
import { SearchableSelect } from "./SearchableSelect";
import { REFINED } from "../../risikoanalyseStep/tokens";
import {
  useBrancheClassification,
  distinctEbene1,
  ebene2ForEbene1,
  leavesFor,
  findLeaf,
  type BrancheLeaf,
} from "../../../data/brancheClassification";

/**
 * Drei kaskadierende Dropdowns für die Courté-Branchenklassifizierung
 * (Datenquelle: Blatt „Spezialisierungen_Einzeln"):
 *
 *   Branche (Ebene 1) → Branchengruppe (Ebene 2) → Spezialisierung (Ebene 3)
 *
 * Der Wert nach außen ist die id des gewählten Ebene-3-Blattes
 * (`brancheClassId`) oder `null`. Unter der Auswahl wird die Erläuterung des
 * Blattes als „(i)"-Hinweis angezeigt. Der Katalog wird modulweit gecacht
 * (useBrancheClassification).
 */
const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
  },
  hint: {
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
    marginTop: "8px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    borderRadius: "8px",
    backgroundColor: REFINED.aub50,
    color: REFINED.ink2,
    fontSize: "0.85rem",
    lineHeight: "1.4",
  },
  hintIcon: {
    flexShrink: 0,
    fontWeight: 800,
    color: REFINED.aub,
  },
});

export const BrancheCascadeSelect: React.FC<{
  value: number | null;
  onChange: (id: number | null, leaf: BrancheLeaf | null) => void;
  required?: boolean;
}> = ({ value, onChange, required }) => {
  const styles = useStyles();
  const { rows, loading } = useBrancheClassification();
  const selectedLeaf = findLeaf(rows, value) ?? null;

  // Lokaler UI-Zustand der Ober-Ebenen. Aus einem extern gesetzten Blatt
  // (Vorbelegung) nachgezogen, sobald value oder der Katalog sich ändern.
  const [ebene1, setEbene1] = React.useState<string>("");
  const [ebene2, setEbene2] = React.useState<string>("");

  React.useEffect(() => {
    const leaf = findLeaf(rows, value);
    if (leaf) {
      setEbene1(leaf.ebene1);
      setEbene2(leaf.ebene2);
    }
  }, [value, rows]);

  const e1Options = React.useMemo(
    () => distinctEbene1(rows).map((x) => ({ value: x, label: x })),
    [rows],
  );
  const e2Options = React.useMemo(
    () => (ebene1 ? ebene2ForEbene1(rows, ebene1).map((x) => ({ value: x, label: x })) : []),
    [rows, ebene1],
  );
  // Die Spezialisierung trägt die Erläuterung des Katalogs als „ⓘ" mit in die
  // Liste (Hover = Tooltip, Klick = aufklappen). Vorher stand sie erst NACH der
  // Wahl unter dem Feld — beim Auswählen, wo sie gebraucht wird, war sie nicht da.
  const e3Options = React.useMemo(
    () =>
      ebene1 && ebene2
        ? leavesFor(rows, ebene1, ebene2).map((l) => ({
            value: String(l.id),
            label: l.ebene3,
            info: l.erlaeuterung ?? undefined,
          }))
        : [],
    [rows, ebene1, ebene2],
  );

  const handleE1 = (v: string) => {
    setEbene1(v);
    setEbene2("");
    onChange(null, null); // Ober-Ebene geändert → Blatt-Auswahl zurücksetzen
  };
  const handleE2 = (v: string) => {
    setEbene2(v);
    onChange(null, null);
  };
  const handleE3 = (v: string) => {
    const leaf = v ? (findLeaf(rows, Number(v)) ?? null) : null;
    onChange(leaf ? leaf.id : null, leaf);
  };

  return (
    <div>
      <div className={styles.grid}>
        <LabeledSelect
          label="Branche"
          name="brancheE1"
          value={ebene1}
          onChange={handleE1}
          options={e1Options}
          placeholder={loading ? "Lädt…" : "Branche wählen"}
          required={required}
        />
        <LabeledSelect
          label="Branchengruppe"
          name="brancheE2"
          value={ebene2}
          onChange={handleE2}
          options={e2Options}
          placeholder={ebene1 ? "Branchengruppe wählen" : "erst Branche wählen"}
        />
        <SearchableSelect
          label="Spezialisierung"
          name="brancheE3"
          value={value != null ? String(value) : ""}
          onChange={handleE3}
          options={e3Options}
          // Nur gelistete Spezialisierungen — der Katalog ist die Wahrheit.
          allowFreeText={false}
          maxResults={e3Options.length}
          placeholder={ebene2 ? "Spezialisierung wählen" : "erst Branchengruppe wählen"}
          emptyText="Keine Spezialisierung gefunden"
        />
      </div>
      {selectedLeaf?.erlaeuterung ? (
        <div className={styles.hint}>
          <span className={styles.hintIcon} aria-hidden>
            ⓘ
          </span>
          <span>{selectedLeaf.erlaeuterung}</span>
        </div>
      ) : null}
    </div>
  );
};

export default BrancheCascadeSelect;
